/**
 * EventCard component for displaying an event in lists or grids
 * Uses shared components from event-card-shared.tsx
 */

'use client';

import type { EventTeaser } from '../types';
import {
    EventCardCompact,
    EventCardDefault,
    EventCardDefaultNonClickable,
    useEventCardData,
} from './event-card-shared';

export type EventCardLayout = 'grid' | 'list' | 'compact' | 'default' | 'featured';

interface EventCardProps {
  event: EventTeaser;
  /** Base URL for event links (e.g., '/events') */
  baseUrl?: string;
  /** Layout variant for the card */
  variant?: EventCardLayout;
  /** Show past events differently */
  showPastIndicator?: boolean;
  /** Whether the entire card should be clickable (default: true) */
  clickable?: boolean;
  // Legacy props for compatibility (not used but kept for interface compatibility)
  showImage?: boolean;
  showDate?: boolean;
  showLocation?: boolean;
  showEventType?: boolean;
  showExcerpt?: boolean;
}

/**
 * EventCard component displays an event in a card format
 * with image, title, date, location, and other metadata
 * 
 * Supports both clickable (entire card is a link) and non-clickable 
 * (with action buttons) modes, similar to EventThemeCard
 */
export function EventCard({
  event,
  baseUrl = '/events',
  variant = 'default',
  showPastIndicator = true,
  clickable = true,
  // Legacy props (not used)
  showImage,
  showDate,
  showLocation,
  showEventType,
  showExcerpt,
}: EventCardProps) {
  // Construct event URL - event.path already contains full path like /events/title
  const eventUrl = event.path;

  if (!eventUrl) return null;

  // Extract event data using shared hook
  const data = useEventCardData(event);

  // Map layout variants: 'grid'/'list' -> 'default', 'featured' -> 'default'
  const actualVariant = variant === 'featured' || variant === 'grid' || variant === 'list' 
    ? 'default' 
    : variant;

  // For compact variant, always use EventCardCompact (which is clickable)
  if (actualVariant === 'compact') {
    return <EventCardCompact event={event} eventUrl={eventUrl} data={data} />;
  }

  // For default variant, choose between clickable and non-clickable based on prop
  if (!clickable) {
    return (
      <EventCardDefaultNonClickable
        event={event}
        eventUrl={eventUrl}
        data={data}
        showPastIndicator={showPastIndicator}
      />
    );
  }

  return (
    <EventCardDefault
      event={event}
      eventUrl={eventUrl}
      data={data}
      showPastIndicator={showPastIndicator}
    />
  );
}
